import { useRetrieveIdentityProviderMutation } from '../apis/useRetrieveIdentityProviderMutation';
import { useExternalReference } from './useExternalReference';
import type { CapturedApiError } from '@invision/core';
import type {
  SecurityRetrieveIdentityProviderRequest,
  SecurityRetrieveIdentityProviderResponse
} from '../types';
import type { IdpRedirectParams } from '../services/idpRedirectService';

interface NameIdCallbacks {
  onIdpFound: (redirect: IdpRedirectParams) => void;
  onIdpNotFound: (nameId: string) => void;
  onError: (fault: CapturedApiError, nameId: string) => void;
}

interface SourceCallbacks {
  onIdpFound: (redirect: IdpRedirectParams) => void;
  onIdpNotFound: () => void;
  onError: (fault: CapturedApiError) => void;
}

/**
 * Hook for handling external authentication scenarios
 *
 * Provides two main scenarios:
 * - **nameId**: User lookup by username (pre-populates login form, shows password field on failure)
 * - **source**: External system lookup (redirects immediately, shows empty form on failure)
 */
function useExternalReferenceScenarios() {
  const { clearNameIdParams, clearSourceParams } = useExternalReference();
  const { mutate: retrieveIdentityProvider } =
    useRetrieveIdentityProviderMutation();

  /**
   * Handle nameId scenario - lookup user by nameId
   */
  function handleNameIdScenario(nameId: string, callbacks: NameIdCallbacks) {
    const request: SecurityRetrieveIdentityProviderRequest = {
      Login: nameId,
      ExternalReference: null
    };

    retrieveIdentityProvider(request, {
      onSuccess: (result: SecurityRetrieveIdentityProviderResponse) => {
        if (result.IdentityProviderUrl) {
          // IDP found - let parent handle redirect
          callbacks.onIdpFound({
            idpServerUrl: result.IdentityProviderUrl,
            username: nameId,
            samlAuthNRequest: result.SamlAuthNRequest,
            includeRelayState: result.IncludeRelayState,
            targetResourceParamName: result.TargetResourceParamName
          });
        } else {
          // No IDP - clear params and let parent handle UI
          clearNameIdParams();
          callbacks.onIdpNotFound(nameId);
        }
      },
      onError: (fault: CapturedApiError) => {
        // Error - clear params and let parent handle error
        clearNameIdParams();
        callbacks.onError(fault, nameId);
      }
    });
  }

  /**
   * Handle source scenario - lookup external system by source
   */
  function handleSourceScenario(source: string, callbacks: SourceCallbacks) {
    const request: SecurityRetrieveIdentityProviderRequest = {
      Login: null,
      ExternalReference: source
    };

    retrieveIdentityProvider(request, {
      onSuccess: (result: SecurityRetrieveIdentityProviderResponse) => {
        if (result.IdentityProviderUrl) {
          // IDP found - let parent handle redirect
          callbacks.onIdpFound({
            idpServerUrl: result.IdentityProviderUrl,
            username: '',
            samlAuthNRequest: result.SamlAuthNRequest,
            includeRelayState: result.IncludeRelayState,
            targetResourceParamName: result.TargetResourceParamName
          });
        } else {
          // No IDP - clear params and let parent handle UI
          clearSourceParams();
          callbacks.onIdpNotFound();
        }
      },
      onError: (fault: CapturedApiError) => {
        // Error - clear params and let parent handle error
        clearSourceParams();
        callbacks.onError(fault);
      }
    });
  }

  return {
    handleNameIdScenario,
    handleSourceScenario
  };
}

export { useExternalReferenceScenarios };
export type { NameIdCallbacks, SourceCallbacks };
